import { useEffect, useMemo, useState } from 'react'
import { addDays, format, parseISO } from 'date-fns'

import { isPolishHoliday, getPolishHolidayName } from '@czas/shared'

import {
  type AdminWorkSession,
  useAdminSessions,
} from './use-admin-sessions'
import {
  type AdminVacationRequest,
  useAdminVacationRequests,
} from './use-admin-vacation'
import {
  type AdminUser,
  useAdminUsers,
} from './use-admin-users'

export const POLISH_WEEKDAYS = [
  'Poniedziałek',
  'Wtorek',
  'Środa',
  'Czwartek',
  'Piątek',
  'Sobota',
  'Niedziela',
] as const

export const POLISH_WEEKDAYS_SHORT = [
  'Pon',
  'Wt',
  'Śr',
  'Czw',
  'Pt',
  'Sob',
  'Niedz',
] as const

export interface CalendarWeekUser {
  id: string
  name: string
  position: { name: string } | null
}

export interface CalendarWeekWorking {
  user: CalendarWeekUser
  sessions: AdminWorkSession[]
  totalDurationMs: number
  hasOpenSession: boolean
  hasOutsideClinic: boolean
}

export interface CalendarWeekVacation {
  user: CalendarWeekUser
  request: AdminVacationRequest
}

export interface CalendarWeekDay {
  date: string
  weekday: string
  weekdayShort: string
  isToday: boolean
  isPolishHoliday: boolean
  holidayName: string | null
  workingSessions: CalendarWeekWorking[]
  vacations: CalendarWeekVacation[]
  inactiveUsers: CalendarWeekUser[]
}

/**
 * Sesja zapisana w sobotę lub niedzielę. Klinika pracuje pn–pt i weekendy nie
 * mają kolumn w kalendarzu, ale sesje weekendowe potrafią powstać: przy
 * korekcie admina, po auto-close przechodzącym przez północ albo gdy ktoś
 * naprawdę przyszedł. Ukrycie kolumn nie może oznaczać ukrycia godzin —
 * dlatego weekend nie jest kolumną, tylko wyjątkiem zgłaszanym osobno.
 */
export interface WeekendActivityEntry {
  date: string
  weekday: string
  userCount: number
  totalDurationMs: number
  hasOpenSession: boolean
}

export interface CalendarWeekData {
  weekStart: string
  weekEnd: string
  days: CalendarWeekDay[]
  weekendActivity: WeekendActivityEntry[]
  totalActiveUsers: number
  isLoading: boolean
  /** Odświeżanie w tle — dane z poprzedniego tygodnia są nadal na ekranie. */
  isFetching: boolean
  error: unknown
}

/**
 * Liczba dni roboczych w widoku tygodniowym (pon–pt).
 * Soboty i niedziele nie są pokazywane — klinika nie pracuje w weekendy.
 */
export const WORKING_DAYS_COUNT = 5

function todayIso(): string {
  return format(new Date(), 'yyyy-MM-dd')
}

/**
 * Bieżąca data jako 'YYYY-MM-DD', przestawiana o północy.
 *
 * Panel admina bywa otwarty przez całą dobę (dyżurka, tablet na recepcji).
 * Bez tego po północy „dziś" zostawało na wczorajszym dniu aż do odświeżenia
 * strony — podświetlenie kolumny i przycisk „Dziś" wskazywały zły dzień.
 */
export function useTodayIso(): string {
  const [today, setToday] = useState(todayIso)

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>

    function scheduleNextMidnight() {
      const now = new Date()
      const nextMidnight = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate() + 1,
        0,
        0,
        2, // 2 s zapasu, żeby nie trafić w sekundę przed północą
      )
      timer = setTimeout(() => {
        setToday(todayIso())
        scheduleNextMidnight()
      }, nextMidnight.getTime() - now.getTime())
    }

    // iOS w trybie standalone nie przeładowuje dokumentu po wznowieniu z tła,
    // a timery w uśpionej karcie potrafią nie wystrzelić o czasie — po powrocie
    // do aplikacji sprawdzamy datę jeszcze raz.
    function onVisible() {
      if (document.visibilityState === 'visible') setToday(todayIso())
    }

    scheduleNextMidnight()
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      clearTimeout(timer)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [])

  return today
}

/** Różnica w pełnych dniach między dwiema datami 'YYYY-MM-DD' (odporna na DST). */
export function diffDays(aIso: string, bIso: string): number {
  const a = Date.parse(`${aIso}T00:00:00Z`)
  const b = Date.parse(`${bIso}T00:00:00Z`)
  return Math.round((a - b) / 86_400_000)
}

/**
 * Z dowolnej daty oblicza poniedziałek tego samego tygodnia (PL convention).
 * Wejście / wyjście w formacie 'YYYY-MM-DD'.
 */
export function getMondayOfWeek(dateIso: string): string {
  const d = parseISO(`${dateIso}T00:00:00`)
  const day = d.getDay() // 0=niedz, 1=pn, ..., 6=sob
  const daysFromMonday = day === 0 ? 6 : day - 1
  const monday = addDays(d, -daysFromMonday)
  return format(monday, 'yyyy-MM-dd')
}

export function getCurrentWeekStart(): string {
  return getMondayOfWeek(todayIso())
}

/**
 * Indeks dnia (0–4), na którym ma się otworzyć widok tygodnia.
 *
 * Reguła: najbliższy dzisiejszemu dzień WIDOCZNY w tym tygodniu.
 *   - tydzień zawiera dziś            → dziś
 *   - dziś to sobota/niedziela        → piątek (ostatni dzień roboczy)
 *   - tydzień z przeszłości           → piątek (najświeższy dzień)
 *   - tydzień z przyszłości           → poniedziałek (najbliższy dzień)
 *
 * Dzięki temu telefon nigdy nie otwiera się na poniedziałku „bo tak wypadł
 * scroll", tylko na dniu, który admin faktycznie chce zobaczyć.
 */
export function getFocusDayIndex(weekStart: string, today: string): number {
  const offset = diffDays(today, weekStart)
  if (offset < 0) return 0
  if (offset > WORKING_DAYS_COUNT - 1) return WORKING_DAYS_COUNT - 1
  return offset
}

function toCalendarUser(
  src: AdminUser | { id: string; name: string; position: { name: string } | null },
): CalendarWeekUser {
  return {
    id: src.id,
    name: src.name,
    position: src.position ? { name: src.position.name } : null,
  }
}

/**
 * Doba, do której należy sesja — liczona w strefie przeglądarki, tak samo
 * jak robi to widok miesiąca i linki `/admin/day/:date`.
 *
 * Wcześniej widok tygodnia brał `startedAt.slice(0, 10)`, czyli surową datę
 * UTC z API. Latem sesja zaczęta o 00:15 czasu warszawskiego ma w ISO
 * poprzedni dzień, więc ta sama sesja trafiała w tygodniu do poniedziałku,
 * a w miesiącu do wtorku.
 */
export function sessionDayIso(s: AdminWorkSession): string {
  return format(parseISO(s.startedAt), 'yyyy-MM-dd')
}

function durationMs(s: AdminWorkSession): number {
  if (!s.endedAt) return 0
  return parseISO(s.endedAt).getTime() - parseISO(s.startedAt).getTime()
}

const WEEKEND_DOW = new Set([0, 6])

/**
 * Sesje zapisane w sobotę lub niedzielę w podanym zakresie dat.
 *
 * Jedna implementacja dla widoku tygodnia i miesiąca. Wcześniej ta sama reguła
 * była napisana dwa razy — w miesiącu po dniu tygodnia, w tygodniu po offsecie
 * od poniedziałku — i wersja offsetowa była błędna: nie miała górnej granicy,
 * więc przy `keepPreviousData` sesje POPRZEDNIEGO tygodnia (offset 7–13)
 * przechodziły warunek i dni robocze sprzed tygodnia lądowały w sekcji
 * „Praca w weekend", w dodatku z pustą nazwą dnia.
 */
export function weekendEntriesFrom(
  sessions: AdminWorkSession[],
  rangeStartIso: string,
  rangeEndIso: string,
): WeekendActivityEntry[] {
  const byDate = new Map<
    string,
    { users: Set<string>; ms: number; open: boolean }
  >()
  for (const session of sessions) {
    const dayIso = sessionDayIso(session)
    // Dane spoza zakresu odpadają — `keepPreviousData` trzyma na ekranie wynik
    // poprzedniego zapytania aż do odpowiedzi serwera.
    if (dayIso < rangeStartIso || dayIso > rangeEndIso) continue
    if (!WEEKEND_DOW.has(parseISO(`${dayIso}T00:00:00`).getDay())) continue
    const entry = byDate.get(dayIso) ?? {
      users: new Set<string>(),
      ms: 0,
      open: false,
    }
    entry.users.add(session.userId)
    entry.ms += durationMs(session)
    if (!session.endedAt) entry.open = true
    byDate.set(dayIso, entry)
  }
  return [...byDate.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([date, entry]) => ({
      date,
      weekday:
        parseISO(`${date}T00:00:00`).getDay() === 6 ? 'Sobota' : 'Niedziela',
      userCount: entry.users.size,
      totalDurationMs: entry.ms,
      hasOpenSession: entry.open,
    }))
}

export function useCalendarWeek(weekStart: string): CalendarWeekData {
  const weekEnd = useMemo(
    () =>
      format(
        addDays(parseISO(`${weekStart}T00:00:00`), WORKING_DAYS_COUNT - 1),
        'yyyy-MM-dd',
      ),
    [weekStart],
  )

  // Sesje pobieramy do NIEDZIELI, choć siatka pokazuje pn–pt: inaczej praca
  // zapisana w weekend nie miałaby jak się ujawnić.
  const weekendEnd = useMemo(
    () => format(addDays(parseISO(`${weekStart}T00:00:00`), 6), 'yyyy-MM-dd'),
    [weekStart],
  )

  const today = useTodayIso()
  // Tylko tydzień zawierający dzisiaj jest podglądem na żywo — przeszłe
  // tygodnie są zamknięte i odpytywanie ich w kółko nic nie wnosi.
  // Świadomie do PIĄTKU, nie do niedzieli: podgląd na żywo ma sens tylko wtedy,
  // gdy klinika pracuje. W weekend odpytywanie co minutę nic nie wnosi.
  const isCurrentWeek = today >= weekStart && today <= weekEnd

  const sessionsQuery = useAdminSessions(
    { from: weekStart, to: weekendEnd },
    {
      keepPrevious: true,
      ...(isCurrentWeek ? { refetchIntervalMs: 60_000 } : {}),
    },
  )
  // Zakres, nie rok — wniosek 28.12–05.01 musi się pokazać w obu tygodniach,
  // a filtr roczny działa po `startDate` i gubiłby go po sylwestrze.
  const vacationsQuery = useAdminVacationRequests(
    { status: 'APPROVED', from: weekStart, to: weekEnd },
    { keepPrevious: true },
  )
  const usersQuery = useAdminUsers({ active: 'true' })

  const days = useMemo<CalendarWeekDay[]>(() => {
    const allUsers = usersQuery.data?.users ?? []
    const allSessions = sessionsQuery.data?.sessions ?? []
    const allVacations = vacationsQuery.data?.requests ?? []

    // Jeden przebieg po sesjach zamiast filtrowania całej listy w pętli po dniach.
    const byDay = new Map<string, Map<string, AdminWorkSession[]>>()
    for (const s of allSessions) {
      const dayIso = sessionDayIso(s)
      let perUser = byDay.get(dayIso)
      if (!perUser) {
        perUser = new Map<string, AdminWorkSession[]>()
        byDay.set(dayIso, perUser)
      }
      const arr = perUser.get(s.userId)
      if (arr) arr.push(s)
      else perUser.set(s.userId, [s])
    }

    return Array.from({ length: WORKING_DAYS_COUNT }, (_, i) => {
      const dayDate = addDays(parseISO(`${weekStart}T00:00:00`), i)
      const dayIso = format(dayDate, 'yyyy-MM-dd')
      const weekday = POLISH_WEEKDAYS[i] as string
      const weekdayShort = POLISH_WEEKDAYS_SHORT[i] as string
      const isToday = dayIso === today

      // Holiday check via UTC midnight equivalent (consistent with shared)
      const holidayDate = new Date(`${dayIso}T00:00:00Z`)
      const isHoliday = isPolishHoliday(holidayDate)
      const holidayName = isHoliday ? getPolishHolidayName(holidayDate) : null

      const sessionsByUser = byDay.get(dayIso) ?? new Map<string, AdminWorkSession[]>()
      const workingUserIds = new Set(sessionsByUser.keys())

      const workingSessions: CalendarWeekWorking[] = []
      for (const [, sessions] of sessionsByUser.entries()) {
        const userObj = sessions[0]?.user
        if (!userObj) continue
        sessions.sort((a, b) => a.startedAt.localeCompare(b.startedAt))
        const totalDurationMs = sessions.reduce((acc, s) => acc + durationMs(s), 0)
        const hasOpenSession = sessions.some((s) => !s.endedAt)
        const hasOutsideClinic = sessions.some((s) => s.outsideClinic)
        workingSessions.push({
          user: toCalendarUser({
            id: userObj.id,
            name: userObj.name,
            position: userObj.position
              ? { name: userObj.position.name }
              : null,
          }),
          sessions,
          totalDurationMs,
          hasOpenSession,
          hasOutsideClinic,
        })
      }
      workingSessions.sort((a, b) => a.user.name.localeCompare(b.user.name, 'pl'))

      // Vacations covering this day
      const vacationsForDay = allVacations.filter(
        (r) => r.startDate <= dayIso && r.endDate >= dayIso,
      )
      const seenVacationUsers = new Set<string>()
      const vacations: CalendarWeekVacation[] = []
      for (const r of vacationsForDay) {
        if (seenVacationUsers.has(r.userId)) continue
        seenVacationUsers.add(r.userId)
        vacations.push({
          user: toCalendarUser({
            id: r.user.id,
            name: r.user.name,
            position: r.user.position,
          }),
          request: r,
        })
      }
      vacations.sort((a, b) => a.user.name.localeCompare(b.user.name, 'pl'))

      // Inactive = active users not working and not on vacation
      const inactiveUsers: CalendarWeekUser[] = allUsers
        .filter(
          (u) =>
            !workingUserIds.has(u.id) && !seenVacationUsers.has(u.id),
        )
        .map(toCalendarUser)
        .sort((a, b) => a.name.localeCompare(b.name, 'pl'))

      return {
        date: dayIso,
        weekday,
        weekdayShort,
        isToday,
        isPolishHoliday: isHoliday,
        holidayName,
        workingSessions,
        vacations,
        inactiveUsers,
      }
    })
  }, [
    weekStart,
    today,
    sessionsQuery.data,
    vacationsQuery.data,
    usersQuery.data,
  ])

  const weekendActivity = useMemo<WeekendActivityEntry[]>(
    () =>
      weekendEntriesFrom(
        sessionsQuery.data?.sessions ?? [],
        weekStart,
        weekendEnd,
      ),
    [sessionsQuery.data, weekStart, weekendEnd],
  )

  const totalActiveUsers = usersQuery.data?.users?.length ?? 0

  return {
    weekStart,
    weekEnd,
    days,
    weekendActivity,
    totalActiveUsers,
    // `isLoading` tylko przy pierwszym wejściu (brak jakichkolwiek danych).
    // Przy zmianie tygodnia `placeholderData` trzyma poprzedni tydzień
    // na ekranie, a `isFetching` zapala dyskretny wskaźnik odświeżania.
    isLoading:
      sessionsQuery.isLoading ||
      vacationsQuery.isLoading ||
      usersQuery.isLoading,
    isFetching:
      sessionsQuery.isFetching ||
      vacationsQuery.isFetching ||
      usersQuery.isFetching,
    error: sessionsQuery.error ?? vacationsQuery.error ?? usersQuery.error,
  }
}
